import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { GoogleMap, useJsApiLoader, OverlayView } from '@react-google-maps/api';
import { CalendarBooking } from '../lib/bookings';
import { Resource } from '../lib/resources';
import { supabase } from '../lib/supabase';
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, startOfYear, endOfYear, subMonths, subYears } from 'date-fns';
import { addressMarkerService, AddressMarker } from '../services/addressMarkerService';
import { getPersonInitials } from '../utils/personInitials';
import CrmEmbeddedOpportunityModal from './admin-dashboard/CrmEmbeddedOpportunityModal';

const mapContainerStyle = {
  width: '100%',
  height: '100%',
};

const defaultCenter = { lat: 49.2827, lng: -123.1207 };

const libraries: ('places')[] = ['places'];

interface LocationUpdate {
  id: string;
  resource_id: string;
  lat: number;
  lng: number;
  accuracy: number | null;
  speed: number | null;
  heading: number | null;
  activity: string | null;
  created_at: string;
  gps_timestamp: string | null;
}

const RESOURCE_COLORS = ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];

const MARKER_TYPES: { type: AddressMarker['marker_type']; label: string; color: string; icon: string }[] = [
  { type: 'not-home', label: 'Not Home', color: '#9CA3AF', icon: '🏠' },
  { type: 'prospect', label: 'Prospect', color: '#F97316', icon: '🎯' },
  { type: 'booked', label: 'Closed', color: '#10B981', icon: '✅' },
  { type: 'comeback-later', label: 'Come Back Later', color: '#F59E0B', icon: '⏰' },
  { type: 'interested', label: 'Interested', color: '#3B82F6', icon: '👀' },
  { type: 'estimate-sent', label: 'Estimate Sent', color: '#8B5CF6', icon: '📄' },
  { type: 'bad-customer', label: 'Bad Customer', color: '#EF4444', icon: '❌' },
  { type: 'customer', label: 'Customer', color: '#06B6D4', icon: '👤' },
  { type: 'not-interested', label: 'Not Interested', color: '#DC2626', icon: '🚫' },
];

interface AdvancedMapViewProps {
  businessId?: string;
  businessAddress?: string;
  bookings: CalendarBooking[];
  onBookingClick?: (booking: CalendarBooking) => void;
  initialMonth?: Date;
  resources?: Resource[];
  salesPinsEnabled?: boolean;
}

function getStatusColor(status: string): string {
  switch (status) {
    case 'completed': return '#22c55e';
    case 'in_progress': return '#f59e0b';
    case 'cancelled': return '#ef4444';
    default: return '#3b82f6';
  }
}

function toNum(val: any): number | null {
  if (val == null) return null;
  const n = Number(val);
  return isNaN(n) ? null : n;
}

const PIN_SVG = 'M12 0C7.31 0 3.5 3.81 3.5 8.5C3.5 14.88 12 24 12 24S20.5 14.88 20.5 8.5C20.5 3.81 16.69 0 12 0ZM12 11.5C10.34 11.5 9 10.16 9 8.5C9 6.84 10.34 5.5 12 5.5C13.66 5.5 15 6.84 15 8.5C15 10.16 13.66 11.5 12 11.5Z';

export default function AdvancedMapView({ businessId, businessAddress, bookings, onBookingClick, initialMonth, resources = [], salesPinsEnabled: salesPinsEnabledProp = true }: AdvancedMapViewProps) {
  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '',
    libraries,
  });

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => setCurrentUserId(data.user?.id || null));
  }, []);

  type DatePreset = 'this_month' | 'this_week' | 'today' | 'last_month' | 'this_year' | 'last_year' | 'custom';

  const getPresetRange = useCallback((preset: DatePreset): { start: string; end: string } => {
    const now = new Date();
    switch (preset) {
      case 'today':
        return { start: format(now, 'yyyy-MM-dd'), end: format(now, 'yyyy-MM-dd') };
      case 'this_week':
        return { start: format(startOfWeek(now, { weekStartsOn: 0 }), 'yyyy-MM-dd'), end: format(endOfWeek(now, { weekStartsOn: 0 }), 'yyyy-MM-dd') };
      case 'this_month':
        return { start: format(startOfMonth(now), 'yyyy-MM-dd'), end: format(endOfMonth(now), 'yyyy-MM-dd') };
      case 'last_month': {
        const last = subMonths(now, 1);
        return { start: format(startOfMonth(last), 'yyyy-MM-dd'), end: format(endOfMonth(last), 'yyyy-MM-dd') };
      }
      case 'this_year':
        return { start: format(startOfYear(now), 'yyyy-MM-dd'), end: format(endOfYear(now), 'yyyy-MM-dd') };
      case 'last_year': {
        const lastY = subYears(now, 1);
        return { start: format(startOfYear(lastY), 'yyyy-MM-dd'), end: format(endOfYear(lastY), 'yyyy-MM-dd') };
      }
      default:
        return { start: format(startOfMonth(now), 'yyyy-MM-dd'), end: format(endOfMonth(now), 'yyyy-MM-dd') };
    }
  }, []);

  const [selectedPreset, setSelectedPreset] = useState<DatePreset>('this_month');
  const [startDate, setStartDate] = useState(() => {
    const d = initialMonth || new Date();
    return format(startOfMonth(d), 'yyyy-MM-dd');
  });
  const [endDate, setEndDate] = useState(() => {
    const d = initialMonth || new Date();
    return format(endOfMonth(d), 'yyyy-MM-dd');
  });
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(e.target as Node)) {
        setFilterDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handlePresetSelect = useCallback((preset: DatePreset) => {
    setSelectedPreset(preset);
    if (preset !== 'custom') {
      const range = getPresetRange(preset);
      setStartDate(range.start);
      setEndDate(range.end);
      setFilterDropdownOpen(false);
    }
  }, [getPresetRange]);

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [businessLocation, setBusinessLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [selectedBooking, setSelectedBooking] = useState<CalendarBooking | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<{ lat: number; lng: number } | null>(null);
  const businessGeocodedRef = useRef(false);
  const nativeMarkersRef = useRef<google.maps.Marker[]>([]);
  const routePolylinesRef = useRef<google.maps.Polyline[]>([]);
  const routeMarkersRef = useRef<google.maps.Marker[]>([]);

  // Route view state
  const [isRouteModalOpen, setIsRouteModalOpen] = useState(false);
  const [routeDate, setRouteDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedResourceIds, setSelectedResourceIds] = useState<Set<string>>(new Set());
  const [isRouteActive, setIsRouteActive] = useState(false);
  const [routeData, setRouteData] = useState<Map<string, LocationUpdate[]>>(new Map());
  const [isLoadingRoute, setIsLoadingRoute] = useState(false);

  // Sales pins state - controlled by calendar settings prop, with local toggle for layer visibility
  const [salesPinsLocalToggle, setSalesPinsLocalToggle] = useState(true);
  const salesPinsEnabled = salesPinsEnabledProp && salesPinsLocalToggle;
  const [addressMarkers, setAddressMarkers] = useState<AddressMarker[]>([]);
  const [isPlacingPin, setIsPlacingPin] = useState(false);
  const [pendingPinLatLng, setPendingPinLatLng] = useState<{ lat: number; lng: number } | null>(null);
  const [showTypeSelector, setShowTypeSelector] = useState(false);
  const [selectedMarker, setSelectedMarker] = useState<AddressMarker | null>(null);
  const [viewLeadOpportunityId, setViewLeadOpportunityId] = useState<number | null>(null);
  const [selectedMarkerPosition, setSelectedMarkerPosition] = useState<{ lat: number; lng: number } | null>(null);
  const [editingMarkerType, setEditingMarkerType] = useState<AddressMarker['marker_type'] | null>(null);
  const [markerNotes, setMarkerNotes] = useState('');
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressInput, setAddressInput] = useState('');
  const [isGeocodingAddress, setIsGeocodingAddress] = useState(false);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const salesMarkersRef = useRef<google.maps.Marker[]>([]);

  const activeResources = useMemo(() => resources.filter(r => r.status === 'active'), [resources]);

  const toggleResourceSelection = useCallback((resourceId: string) => {
    setSelectedResourceIds(prev => {
      const next = new Set(prev);
      if (next.has(resourceId)) next.delete(resourceId);
      else next.add(resourceId);
      return next;
    });
  }, []);

  const handleApplyRoute = useCallback(async () => {
    if (!businessId || selectedResourceIds.size === 0) return;
    setIsLoadingRoute(true);

    try {
      const dayStart = `${routeDate}T00:00:00`;
      const dayEnd = `${routeDate}T23:59:59`;

      const resourceIds = Array.from(selectedResourceIds);

      // Paginate to avoid Supabase 1000-row default limit
      let allData: LocationUpdate[] = [];
      let page = 0;
      const pageSize = 5000;
      let hasMore = true;

      while (hasMore) {
        const from = page * pageSize;
        const to = from + pageSize - 1;

        const { data: pageData, error } = await supabase
          .from('location_updates')
          .select('id, resource_id, lat, lng, accuracy, speed, heading, activity, created_at, gps_timestamp')
          .eq('business_id', businessId)
          .in('resource_id', resourceIds)
          .gte('created_at', dayStart)
          .lte('created_at', dayEnd)
          .order('created_at', { ascending: true })
          .range(from, to);

        if (error) throw error;

        allData = allData.concat(pageData || []);
        hasMore = (pageData?.length || 0) === pageSize;
        page++;
      }

      console.log(`🗺️ Fetched ${allData.length} location updates across ${page} page(s)`);

      const grouped = new Map<string, LocationUpdate[]>();
      allData.forEach((update: LocationUpdate) => {
        const existing = grouped.get(update.resource_id) || [];
        existing.push(update);
        grouped.set(update.resource_id, existing);
      });

      setRouteData(grouped);
      setIsRouteActive(true);
      setStartDate(routeDate);
      setEndDate(routeDate);
      setSelectedPreset('custom');
      setIsRouteModalOpen(false);
    } catch (err) {
      console.error('Error fetching route data:', err);
    } finally {
      setIsLoadingRoute(false);
    }
  }, [businessId, selectedResourceIds, routeDate]);

  const directionsRenderersRef = useRef<google.maps.DirectionsRenderer[]>([]);

  const handleClearRoute = useCallback(() => {
    setIsRouteActive(false);
    setRouteData(new Map());
    routePolylinesRef.current.forEach(p => p.setMap(null));
    routePolylinesRef.current = [];
    routeMarkersRef.current.forEach(m => m.setMap(null));
    routeMarkersRef.current = [];
    directionsRenderersRef.current.forEach(r => r.setMap(null));
    directionsRenderersRef.current = [];
  }, []);

  // --- Sales pin helpers ---
  const loadAddressMarkers = useCallback(async () => {
    if (!businessId) return;
    try {
      const markers = await addressMarkerService.getMarkers(businessId);
      setAddressMarkers(markers);
    } catch (err) {
      console.error('Error loading address markers:', err);
    }
  }, [businessId]);

  useEffect(() => {
    if (salesPinsEnabled && businessId) {
      loadAddressMarkers();
    }
    // Refresh instantly when a pin is dropped from the FAB sales pin flow
    const onPinCreated = () => {
      if (salesPinsEnabled && businessId) loadAddressMarkers();
    };
    window.addEventListener('salesPinCreated', onPinCreated);
    return () => window.removeEventListener('salesPinCreated', onPinCreated);
  }, [salesPinsEnabled, businessId, loadAddressMarkers]);

  // Render sales markers on the map
  useEffect(() => {
    if (!map || !isLoaded) return;

    salesMarkersRef.current.forEach(m => m.setMap(null));
    salesMarkersRef.current = [];

    if (!salesPinsEnabled) return;

    addressMarkers.forEach(marker => {
      const mt = MARKER_TYPES.find(t => t.type === marker.marker_type);
      const position = { lat: marker.latitude, lng: marker.longitude };

      const gMarker = new google.maps.Marker({
        map,
        position,
        icon: addressMarkerService.getSalesPinIcon(
          marker.marker_type,
          addressMarkerService.getSalesPinInitials(marker),
        ),
        title: mt?.label || marker.marker_type,
        zIndex: 5,
      });

      gMarker.addListener('click', () => {
        setSelectedBooking(null);
        setSelectedPosition(null);
        setSelectedMarker(marker);
        setSelectedMarkerPosition(position);
        setEditingMarkerType(marker.marker_type);
        setMarkerNotes(marker.notes || '');
      });

      salesMarkersRef.current.push(gMarker);
    });

    return () => {
      salesMarkersRef.current.forEach(m => m.setMap(null));
      salesMarkersRef.current = [];
    };
  }, [map, isLoaded, salesPinsEnabled, addressMarkers]);

  const handleMapClick = useCallback((e: google.maps.MapMouseEvent) => {
    if (!isPlacingPin || !e.latLng) return;
    setPendingPinLatLng({ lat: e.latLng.lat(), lng: e.latLng.lng() });
    setShowTypeSelector(true);
  }, [isPlacingPin]);

  const handleCreateMarker = useCallback(async (type: AddressMarker['marker_type']) => {
    if (!businessId || !pendingPinLatLng || !currentUserId) return;
    try {
      await addressMarkerService.createMarker({
        business_id: businessId,
        latitude: pendingPinLatLng.lat,
        longitude: pendingPinLatLng.lng,
        marker_type: type,
        created_by: currentUserId,
      });
      await loadAddressMarkers();
    } catch (err) {
      console.error('Error creating marker:', err);
    } finally {
      setPendingPinLatLng(null);
      setShowTypeSelector(false);
      setIsPlacingPin(false);
    }
  }, [businessId, pendingPinLatLng, loadAddressMarkers]);

  const handleUpdateMarker = useCallback(async () => {
    if (!selectedMarker?.id || !editingMarkerType) return;
    try {
      await addressMarkerService.updateMarker(selectedMarker.id, {
        marker_type: editingMarkerType,
        notes: markerNotes || undefined,
      });
      await loadAddressMarkers();
    } catch (err) {
      console.error('Error updating marker:', err);
    } finally {
      setSelectedMarker(null);
      setSelectedMarkerPosition(null);
    }
  }, [selectedMarker, editingMarkerType, markerNotes, loadAddressMarkers]);

  const handleDeleteMarker = useCallback(async () => {
    if (!selectedMarker?.id) return;
    try {
      await addressMarkerService.deleteMarker(selectedMarker.id);
      await loadAddressMarkers();
    } catch (err) {
      console.error('Error deleting marker:', err);
    } finally {
      setSelectedMarker(null);
      setSelectedMarkerPosition(null);
    }
  }, [selectedMarker, loadAddressMarkers]);

  // Setup Places Autocomplete when address modal opens
  useEffect(() => {
    if (!showAddressModal || !isLoaded || !addressInputRef.current) return;

    const timeout = setTimeout(() => {
      if (!addressInputRef.current) return;
      const ac = new google.maps.places.Autocomplete(addressInputRef.current, {
        types: ['address'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (place.formatted_address) {
          setAddressInput(place.formatted_address);
        }
      });
      autocompleteRef.current = ac;
    }, 100);

    return () => {
      clearTimeout(timeout);
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current);
        autocompleteRef.current = null;
      }
    };
  }, [showAddressModal, isLoaded]);

  const handleAddressPinSubmit = useCallback(async () => {
    if (!addressInput.trim() || !isLoaded) return;
    setIsGeocodingAddress(true);
    try {
      const geocoder = new google.maps.Geocoder();
      const result = await new Promise<google.maps.GeocoderResult[]>((resolve, reject) => {
        geocoder.geocode({ address: addressInput.trim() }, (results, status) => {
          if (status === 'OK' && results?.length) resolve(results);
          else reject(new Error(`Geocoding failed: ${status}`));
        });
      });
      const loc = result[0].geometry.location;
      const latLng = { lat: loc.lat(), lng: loc.lng() };
      setPendingPinLatLng(latLng);
      setShowAddressModal(false);
      setShowTypeSelector(true);
      if (map) {
        map.panTo(latLng);
        if ((map.getZoom() || 10) < 15) map.setZoom(15);
      }
    } catch (err) {
      console.error('Geocoding error:', err);
      alert('Could not find that address. Please try a more specific address.');
    } finally {
      setIsGeocodingAddress(false);
    }
  }, [addressInput, isLoaded, map]);

  // Store the resolved address on the marker when creating from address search
  const handleCreateMarkerWithAddress = useCallback(async (type: AddressMarker['marker_type']) => {
    if (!businessId || !pendingPinLatLng || !currentUserId) return;
    try {
      await addressMarkerService.createMarker({
        business_id: businessId,
        latitude: pendingPinLatLng.lat,
        longitude: pendingPinLatLng.lng,
        marker_type: type,
        address: addressInput.trim() || undefined,
        created_by: currentUserId,
      });
      await loadAddressMarkers();
    } catch (err) {
      console.error('Error creating marker:', err);
    } finally {
      setPendingPinLatLng(null);
      setShowTypeSelector(false);
      setIsPlacingPin(false);
      setAddressInput('');
    }
  }, [businessId, pendingPinLatLng, addressInput, loadAddressMarkers]);

  // Sample evenly spaced waypoints from a list of points (Directions API max 25 waypoints)
  const sampleWaypoints = useCallback((points: { lat: number; lng: number }[], maxWaypoints: number) => {
    if (points.length <= maxWaypoints + 2) return points.slice(1, -1);
    const step = (points.length - 2) / (maxWaypoints);
    const sampled: { lat: number; lng: number }[] = [];
    for (let i = 0; i < maxWaypoints; i++) {
      const idx = Math.round(1 + i * step);
      if (idx < points.length - 1) sampled.push(points[idx]);
    }
    return sampled;
  }, []);

  // Draw route on actual roads using Directions Service
  useEffect(() => {
    if (!map || !isLoaded) return;

    // Clean up previous renderings
    routePolylinesRef.current.forEach(p => p.setMap(null));
    routePolylinesRef.current = [];
    routeMarkersRef.current.forEach(m => m.setMap(null));
    routeMarkersRef.current = [];
    directionsRenderersRef.current.forEach(r => r.setMap(null));
    directionsRenderersRef.current = [];

    if (!isRouteActive || routeData.size === 0) return;

    const directionsService = new google.maps.DirectionsService();
    const bounds = new google.maps.LatLngBounds();
    if (businessLocation) bounds.extend(businessLocation);

    const resourceIdArray = Array.from(selectedResourceIds);

    routeData.forEach((updates, resourceId) => {
      if (updates.length < 2) return;

      const color = RESOURCE_COLORS[resourceIdArray.indexOf(resourceId) % RESOURCE_COLORS.length];
      const allPoints = updates.map(u => ({ lat: u.lat, lng: u.lng }));

      const origin = allPoints[0];
      const destination = allPoints[allPoints.length - 1];
      bounds.extend(origin);
      bounds.extend(destination);

      // Break into chunks if needed (max 23 waypoints per request)
      const MAX_WAYPOINTS = 23;
      const chunks: { origin: google.maps.LatLngLiteral; destination: google.maps.LatLngLiteral; waypoints: google.maps.DirectionsWaypoint[] }[] = [];

      if (allPoints.length <= MAX_WAYPOINTS + 2) {
        const waypoints = allPoints.slice(1, -1).map(p => ({ location: p, stopover: false }));
        chunks.push({ origin, destination, waypoints });
      } else {
        const chunkSize = MAX_WAYPOINTS + 2;
        for (let i = 0; i < allPoints.length - 1; i += MAX_WAYPOINTS + 1) {
          const chunkPoints = allPoints.slice(i, Math.min(i + chunkSize, allPoints.length));
          if (chunkPoints.length < 2) break;
          const cOrigin = chunkPoints[0];
          const cDest = chunkPoints[chunkPoints.length - 1];
          const cWaypoints = chunkPoints.slice(1, -1).map(p => ({ location: p, stopover: false }));
          chunks.push({ origin: cOrigin, destination: cDest, waypoints: cWaypoints });
        }
      }

      // Request directions for each chunk
      chunks.forEach((chunk, chunkIdx) => {
        directionsService.route(
          {
            origin: chunk.origin,
            destination: chunk.destination,
            waypoints: chunk.waypoints,
            travelMode: google.maps.TravelMode.DRIVING,
            optimizeWaypoints: false,
          },
          (result, status) => {
            if (status === 'OK' && result) {
              const renderer = new google.maps.DirectionsRenderer({
                map,
                directions: result,
                suppressMarkers: true,
                polylineOptions: {
                  strokeColor: color,
                  strokeOpacity: 0.85,
                  strokeWeight: 5,
                },
                preserveViewport: true,
              });
              directionsRenderersRef.current.push(renderer);

              // Add start/end markers only on first/last chunk
              if (chunkIdx === 0) {
                const startMarker = new google.maps.Marker({
                  map,
                  position: origin,
                  icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    fillColor: color,
                    fillOpacity: 1,
                    strokeColor: '#ffffff',
                    strokeWeight: 2,
                    scale: 8,
                  },
                  title: 'Start',
                  zIndex: 10,
                });
                routeMarkersRef.current.push(startMarker);
              }
              if (chunkIdx === chunks.length - 1) {
                const endMarker = new google.maps.Marker({
                  map,
                  position: destination,
                  icon: {
                    path: google.maps.SymbolPath.CIRCLE,
                    fillColor: '#ffffff',
                    fillOpacity: 1,
                    strokeColor: color,
                    strokeWeight: 3,
                    scale: 8,
                  },
                  title: 'End',
                  zIndex: 10,
                });
                routeMarkersRef.current.push(endMarker);
              }
            } else {
              console.warn('Directions request failed:', status, '- falling back to polyline');
              // Fallback: draw straight polyline if directions fail
              const polyline = new google.maps.Polyline({
                map,
                path: [chunk.origin, ...chunk.waypoints.map(w => w.location as google.maps.LatLngLiteral), chunk.destination],
                strokeColor: color,
                strokeOpacity: 0.6,
                strokeWeight: 3,
                geodesic: true,
              });
              routePolylinesRef.current.push(polyline);
            }
          }
        );
      });
    });

    map.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });

    return () => {
      routePolylinesRef.current.forEach(p => p.setMap(null));
      routePolylinesRef.current = [];
      routeMarkersRef.current.forEach(m => m.setMap(null));
      routeMarkersRef.current = [];
      directionsRenderersRef.current.forEach(r => r.setMap(null));
      directionsRenderersRef.current = [];
    };
  }, [map, isLoaded, isRouteActive, routeData, businessLocation, selectedResourceIds]);

  // Geocode business address
  useEffect(() => {
    if (!isLoaded || !businessAddress || businessGeocodedRef.current) return;
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ address: businessAddress }, (results, status) => {
      if (status === 'OK' && results?.[0]) {
        const loc = results[0].geometry.location;
        setBusinessLocation({ lat: loc.lat(), lng: loc.lng() });
        businessGeocodedRef.current = true;
      }
    });
  }, [isLoaded, businessAddress]);

  // Filter bookings by selected date range + mobile only
  const pins = useMemo(() => {
    const rangeStart = new Date(`${startDate}T00:00:00`);
    const rangeEnd = new Date(`${endDate}T23:59:59`);

    const result: { booking: CalendarBooking; lat: number; lng: number }[] = [];
    bookings.forEach(b => {
      if (!b.extendedProps?.mobile_booking || b.extendedProps?.is_buffer_block) return;

      const bookingStart = new Date(b.start);
      const bookingEnd = b.end ? new Date(b.end) : bookingStart;
      if (bookingEnd < rangeStart || bookingStart > rangeEnd) return;

      const lat = toNum(b.extendedProps?.lat);
      const lng = toNum(b.extendedProps?.lng);
      if (lat !== null && lng !== null) {
        result.push({ booking: b, lat, lng });
      }
    });
    return result;
  }, [bookings, startDate, endDate]);

  // Create native Google Maps markers (hidden during route view)
  useEffect(() => {
    if (!map || !isLoaded) return;
    if (isRouteActive) {
      nativeMarkersRef.current.forEach(m => m.setMap(null));
      nativeMarkersRef.current = [];
      return;
    }

    nativeMarkersRef.current.forEach(m => m.setMap(null));
    nativeMarkersRef.current = [];

    const bounds = new google.maps.LatLngBounds();
    if (businessLocation) bounds.extend(businessLocation);

    pins.forEach((pin) => {
      const position = { lat: pin.lat, lng: pin.lng };
      const color = getStatusColor(pin.booking.extendedProps.status);

      const marker = new google.maps.Marker({
        map,
        position,
        icon: {
          path: PIN_SVG,
          fillColor: color,
          fillOpacity: 1,
          strokeColor: '#ffffff',
          strokeWeight: 2,
          scale: 1.5,
          anchor: new google.maps.Point(12, 24),
        },
        title: `#${pin.booking.booking_number} ${pin.booking.title}`,
      });

      marker.addListener('click', () => {
        setSelectedMarker(null);
        setSelectedMarkerPosition(null);
        setSelectedBooking(pin.booking);
        setSelectedPosition(position);
      });

      nativeMarkersRef.current.push(marker);
      bounds.extend(position);
    });

    if (pins.length > 0 || businessLocation) {
      map.fitBounds(bounds, { top: 50, bottom: 50, left: 50, right: 50 });
    }

    return () => {
      nativeMarkersRef.current.forEach(m => m.setMap(null));
      nativeMarkersRef.current = [];
    };
  }, [map, pins, businessLocation, isLoaded, isRouteActive]);

  const onMapLoad = useCallback((m: google.maps.Map) => {
    setMap(m);
  }, []);

  const mapOptions = useMemo<google.maps.MapOptions>(() => ({
    disableDefaultUI: true,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    gestureHandling: 'greedy',
    draggableCursor: isPlacingPin ? 'crosshair' : undefined,
    styles: [
      { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
      { featureType: 'transit', elementType: 'labels', stylers: [{ visibility: 'off' }] },
    ],
  }), [isPlacingPin]);

  if (!isLoaded) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500">Loading map...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full relative flex flex-col">
      {/* Advanced Settings Panel */}
      <div
        className="bg-white border-b border-gray-200 px-4 py-2.5 flex items-center gap-4 flex-shrink-0 z-10"
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
      >
        <h3 className="text-sm font-semibold text-gray-800">Advanced Map</h3>

        {/* Date range filter dropdown */}
        <div className="relative" ref={filterRef}>
          <button
            onClick={() => setFilterDropdownOpen(prev => !prev)}
            className="flex items-center gap-2 text-xs font-medium border border-gray-300 rounded-lg px-3 py-1.5 bg-gray-50 hover:bg-white transition-colors cursor-pointer"
          >
            <svg className="h-3.5 w-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-gray-700">
              {selectedPreset === 'custom'
                ? `${format(new Date(startDate + 'T00:00:00'), 'MMM d, yyyy')} - ${format(new Date(endDate + 'T00:00:00'), 'MMM d, yyyy')}`
                : { this_month: 'This Month', this_week: 'This Week', today: 'Today', last_month: 'Last Month', this_year: 'This Year', last_year: 'Last Year' }[selectedPreset]
              }
            </span>
            <svg className={`h-3 w-3 text-gray-400 transition-transform ${filterDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {filterDropdownOpen && (
            <div
              className="absolute left-0 top-full mt-1 bg-white rounded-xl shadow-lg border border-gray-200 py-1 z-[60] min-w-[200px]"
              style={{ boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)' }}
            >
              {([
                { key: 'today', label: 'Today' },
                { key: 'this_week', label: 'This Week' },
                { key: 'this_month', label: 'This Month' },
                { key: 'last_month', label: 'Last Month' },
                { key: 'this_year', label: 'This Year' },
                { key: 'last_year', label: 'Last Year' },
                { key: 'custom', label: 'Custom Range' },
              ] as { key: DatePreset; label: string }[]).map((option) => (
                <button
                  key={option.key}
                  onClick={() => handlePresetSelect(option.key)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm transition-colors ${
                    selectedPreset === option.key ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {selectedPreset === option.key && (
                    <svg className="h-3.5 w-3.5 text-blue-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  <span className={selectedPreset === option.key ? '' : 'ml-6'}>{option.label}</span>
                </button>
              ))}

              {/* Custom date inputs */}
              {selectedPreset === 'custom' && (
                <div className="border-t border-gray-100 px-3 py-2 space-y-2">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 font-medium w-10">From</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="flex-1 text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-500 font-medium w-10">To</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="flex-1 text-xs border border-gray-300 rounded-lg px-2 py-1.5 bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* View Route button */}
        <button
          onClick={() => {
            if (isRouteActive) {
              handleClearRoute();
            } else {
              setIsRouteModalOpen(true);
            }
          }}
          className={`flex items-center gap-1.5 text-xs font-medium border rounded-lg px-3 py-1.5 transition-colors cursor-pointer ${
            isRouteActive
              ? 'border-red-300 bg-red-50 text-red-700 hover:bg-red-100'
              : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-white'
          }`}
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          {isRouteActive ? 'Clear Route' : 'View Route'}
        </button>

        {/* Sales Pins toggle - only visible when setting is enabled */}
        {salesPinsEnabledProp && (
          <button
            onClick={() => {
              setSalesPinsLocalToggle(prev => !prev);
              if (isPlacingPin) setIsPlacingPin(false);
            }}
            className={`flex items-center gap-1.5 text-xs font-medium border rounded-lg px-3 py-1.5 transition-colors cursor-pointer ${
              salesPinsEnabled
                ? 'border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100'
                : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-white'
            }`}
          >
            <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Sales Pins
          </button>
        )}

        {/* Place Pin + Add by Address buttons (only visible when sales pins enabled) */}
        {salesPinsEnabled && (
          <>
            <button
              onClick={() => { setIsPlacingPin(prev => !prev); setShowAddressModal(false); }}
              className={`flex items-center gap-1.5 text-xs font-medium border rounded-lg px-3 py-1.5 transition-colors cursor-pointer ${
                isPlacingPin
                  ? 'border-green-300 bg-green-50 text-green-700 hover:bg-green-100 ring-2 ring-green-200'
                  : 'border-gray-300 bg-gray-50 text-gray-700 hover:bg-white'
              }`}
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              {isPlacingPin ? 'Cancel' : 'Drop Pin'}
            </button>
            <button
              onClick={() => { setShowAddressModal(true); setIsPlacingPin(false); setAddressInput(''); }}
              className="flex items-center gap-1.5 text-xs font-medium border border-gray-300 bg-gray-50 text-gray-700 hover:bg-white rounded-lg px-3 py-1.5 transition-colors cursor-pointer"
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search Address
            </button>
          </>
        )}

        <span className="text-xs text-gray-400 ml-auto">
          {isRouteActive
            ? `${routeData.size} resource route${routeData.size !== 1 ? 's' : ''}`
            : salesPinsEnabled
            ? `${addressMarkers.length} pins · ${pins.length} bookings`
            : `${pins.length} bookings`
          }
        </span>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={defaultCenter}
          zoom={10}
          options={mapOptions}
          onLoad={onMapLoad}
          onClick={handleMapClick}
        >
          {/* Business home pin */}
          {businessLocation && (
            <OverlayView
              position={businessLocation}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
            >
              <div className="flex flex-col items-center" style={{ transform: 'translate(-50%, -100%)' }}>
                <div className="bg-white rounded-full p-2 shadow-lg border-2 border-blue-500">
                  <svg className="h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z" />
                  </svg>
                </div>
                <div className="w-0 h-0 border-l-[6px] border-r-[6px] border-t-[8px] border-l-transparent border-r-transparent border-t-blue-500 -mt-[2px]" />
              </div>
            </OverlayView>
          )}

          {/* Popup for selected booking */}
          {selectedBooking && selectedPosition && (
            <OverlayView
              position={selectedPosition}
              mapPaneName={OverlayView.FLOAT_PANE}
            >
              <div
                className="bg-white rounded-xl shadow-xl border border-gray-200 p-3 min-w-[240px] max-w-[300px]"
                style={{ transform: 'translate(-50%, calc(-100% - 40px))', position: 'relative', zIndex: 9999 }}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={(e) => { e.stopPropagation(); setSelectedBooking(null); setSelectedPosition(null); }}
                  className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
                >
                  <span className="text-gray-500 text-sm font-medium">×</span>
                </button>

                <h3 className="font-semibold text-gray-900 text-sm mb-1 pr-6">
                  {selectedBooking.booking_number > 0 && `#${selectedBooking.booking_number} `}{selectedBooking.title}
                </h3>
                <p className="text-xs text-gray-600 mb-1">
                  {format(new Date(selectedBooking.start), 'MMM d')} · {format(new Date(selectedBooking.start), 'h:mm a')} - {format(selectedBooking.end ? new Date(selectedBooking.end) : new Date(new Date(selectedBooking.start).getTime() + 3600000), 'h:mm a')}
                </p>
                {selectedBooking.extendedProps.customer && (
                  <p className="text-xs text-gray-700 font-medium mb-1">{String(selectedBooking.extendedProps.customer)}</p>
                )}
                {(selectedBooking.extendedProps.address || selectedBooking.extendedProps.booking_address) && (
                  <p className="text-xs text-gray-500 mb-2">{selectedBooking.extendedProps.address || selectedBooking.extendedProps.booking_address}</p>
                )}

                <div className="flex gap-2">
                  <button
                    onClick={(e) => { e.stopPropagation(); onBookingClick?.(selectedBooking); setSelectedBooking(null); setSelectedPosition(null); }}
                    className="flex-1 px-3 py-2 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    View Details
                  </button>
                  {(selectedBooking.extendedProps.address || selectedBooking.extendedProps.booking_address) && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const addr = selectedBooking.extendedProps.address || selectedBooking.extendedProps.booking_address;
                        window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addr || '')}`, '_blank');
                      }}
                      className="px-3 py-2 bg-green-500 text-white text-xs font-medium rounded-lg hover:bg-green-600 transition-colors"
                    >
                      Navigate
                    </button>
                  )}
                </div>

                <div className="absolute left-1/2 -bottom-2 w-4 h-4 bg-white border-r border-b border-gray-200 -translate-x-1/2 rotate-45" style={{ zIndex: -1 }} />
              </div>
            </OverlayView>
          )}
          {/* Type selector popup for new pin */}
          {showTypeSelector && pendingPinLatLng && (
            <OverlayView
              position={pendingPinLatLng}
              mapPaneName={OverlayView.FLOAT_PANE}
            >
              <div
                className="bg-white rounded-xl shadow-xl border border-gray-200 p-3 min-w-[200px]"
                style={{ transform: 'translate(-50%, calc(-100% - 16px))', zIndex: 9999 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-semibold text-gray-700">Select Pin Type</p>
                  <button
                    onClick={() => { setShowTypeSelector(false); setPendingPinLatLng(null); setIsPlacingPin(false); }}
                    className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-400 text-sm"
                  >×</button>
                </div>
                <div className="space-y-0.5">
                  {MARKER_TYPES.map(mt => (
                    <button
                      key={mt.type}
                      onClick={() => addressInput.trim() ? handleCreateMarkerWithAddress(mt.type) : handleCreateMarker(mt.type)}
                      className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-gray-50 transition-colors text-left"
                    >
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: mt.color }} />
                      <span className="text-[13px] text-gray-700 font-medium">{mt.icon} {mt.label}</span>
                    </button>
                  ))}
                </div>
                <div className="absolute left-1/2 -bottom-2 w-4 h-4 bg-white border-r border-b border-gray-200 -translate-x-1/2 rotate-45" style={{ zIndex: -1 }} />
              </div>
            </OverlayView>
          )}

          {/* Edit popup for existing sales pin */}
          {selectedMarker && selectedMarkerPosition && (
            <OverlayView
              position={selectedMarkerPosition}
              mapPaneName={OverlayView.FLOAT_PANE}
            >
              <div
                className="bg-white rounded-xl shadow-xl border border-gray-200 p-3 min-w-[260px] max-w-[320px]"
                style={{ transform: 'translate(-50%, calc(-100% - 40px))', zIndex: 9999 }}
                onClick={e => e.stopPropagation()}
              >
                <button
                  onClick={() => { setSelectedMarker(null); setSelectedMarkerPosition(null); }}
                  className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
                >
                  <span className="text-gray-500 text-sm font-medium">×</span>
                </button>

                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Edit Pin</p>

                {selectedMarker.address && (
                  <p className="text-xs text-gray-600 mb-2 truncate">{selectedMarker.address}</p>
                )}
                {selectedMarker.customer && (
                  <p className="text-xs text-gray-700 font-medium mb-2">
                    {selectedMarker.customer.first_name} {selectedMarker.customer.last_name}
                  </p>
                )}

                {(selectedMarker.google_place_name || selectedMarker.google_place_phone || selectedMarker.google_place_website) && (
                  <div className="mb-2 px-2.5 py-2 bg-blue-50/70 border border-blue-100 rounded-lg">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-blue-500 mb-0.5">Google Business</p>
                    {selectedMarker.google_place_name && (
                      <p className="text-xs font-semibold text-gray-900">{selectedMarker.google_place_name}</p>
                    )}
                    {selectedMarker.google_place_phone && (
                      <a
                        href={`tel:${selectedMarker.google_place_phone}`}
                        onClick={e => e.stopPropagation()}
                        className="block text-xs text-blue-700 mt-0.5"
                      >
                        {selectedMarker.google_place_phone}
                      </a>
                    )}
                    {selectedMarker.google_place_website && (
                      <a
                        href={selectedMarker.google_place_website}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="block text-xs text-blue-700 mt-0.5 truncate"
                      >
                        {selectedMarker.google_place_website}
                      </a>
                    )}
                  </div>
                )}

                {selectedMarker.opportunity_id && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setViewLeadOpportunityId(Number(selectedMarker.opportunity_id));
                    }}
                    className="w-full mb-2 px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    View Lead
                  </button>
                )}

                <div className="grid grid-cols-2 gap-1 mb-3">
                  {MARKER_TYPES.map(mt => (
                    <button
                      key={mt.type}
                      onClick={() => setEditingMarkerType(mt.type)}
                      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-[11px] font-medium transition-colors ${
                        editingMarkerType === mt.type
                          ? 'ring-2 ring-offset-1 bg-gray-50'
                          : 'hover:bg-gray-50'
                      }`}
                      style={editingMarkerType === mt.type ? { ringColor: mt.color } : undefined}
                    >
                      <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: mt.color }} />
                      {mt.label}
                    </button>
                  ))}
                </div>

                <textarea
                  value={markerNotes}
                  onChange={e => setMarkerNotes(e.target.value)}
                  onMouseDown={e => e.stopPropagation()}
                  onTouchStart={e => e.stopPropagation()}
                  onFocus={e => e.stopPropagation()}
                  onClick={e => e.stopPropagation()}
                  placeholder="Notes (optional)"
                  className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-2 mb-3 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  rows={2}
                />

                <div className="flex gap-2">
                  <button
                    onClick={handleUpdateMarker}
                    className="flex-1 px-3 py-2 bg-blue-500 text-white text-xs font-medium rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={handleDeleteMarker}
                    className="px-3 py-2 bg-red-50 text-red-600 text-xs font-medium rounded-lg hover:bg-red-100 transition-colors"
                  >
                    Delete
                  </button>
                </div>

                <div className="absolute left-1/2 -bottom-2 w-4 h-4 bg-white border-r border-b border-gray-200 -translate-x-1/2 rotate-45" style={{ zIndex: -1 }} />
              </div>
            </OverlayView>
          )}
        </GoogleMap>

        {/* Placing pin banner */}
        {isPlacingPin && (
          <div className="absolute top-[52px] left-1/2 -translate-x-1/2 bg-green-600 text-white text-xs font-medium px-4 py-2 rounded-full shadow-lg z-20 pointer-events-none">
            Click anywhere on the map to place a pin
          </div>
        )}

        {/* Sales pins legend overlay */}
        {salesPinsEnabled && addressMarkers.length > 0 && !isRouteActive && (
          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200 p-3 z-10">
            <p className="text-xs font-semibold text-gray-700 mb-2">Sales Pins</p>
            <div className="space-y-1">
              {MARKER_TYPES.map(mt => {
                const count = addressMarkers.filter(m => m.marker_type === mt.type).length;
                if (count === 0) return null;
                return (
                  <div key={mt.type} className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: mt.color }} />
                    <span className="text-xs text-gray-700">{mt.label}</span>
                    <span className="text-xs text-gray-400 ml-auto">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Route legend overlay */}
        {isRouteActive && routeData.size > 0 && (
          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-xl shadow-lg border border-gray-200 p-3 z-10">
            <p className="text-xs font-semibold text-gray-700 mb-2">{format(new Date(routeDate + 'T00:00:00'), 'EEEE, MMM d, yyyy')}</p>
            <div className="space-y-1.5">
              {Array.from(selectedResourceIds).map((rid, idx) => {
                const resource = activeResources.find(r => r.id === rid);
                const color = RESOURCE_COLORS[idx % RESOURCE_COLORS.length];
                const updates = routeData.get(rid) || [];
                return (
                  <div key={rid} className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />
                    <span className="text-xs text-gray-700 font-medium">
                      {resource ? `${resource.first_name} ${resource.last_name}` : 'Unknown'}
                    </span>
                    <span className="text-xs text-gray-400">{updates.length} points</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* View Route Modal (right-side mounted) */}
      {isRouteModalOpen && createPortal(
        <div
          className={`fixed inset-0 transition-opacity duration-200 bg-black/50`}
          style={{ zIndex: 99999, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
          onClick={() => setIsRouteModalOpen(false)}
        >
          <div
            className={`fixed inset-y-0 right-0 w-full sm:w-[420px] bg-white shadow-2xl transform transition-transform duration-200 translate-x-0`}
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif', zIndex: 99999 }}
            onClick={e => e.stopPropagation()}
          >
            <div className="h-full flex flex-col overflow-hidden">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gray-50">
                <h2 className="text-lg font-bold text-gray-900">View Route</h2>
                <button
                  onClick={() => setIsRouteModalOpen(false)}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 transition-colors"
                >
                  <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
                {/* Date picker */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Select Date</label>
                  <input
                    type="date"
                    value={routeDate}
                    onChange={(e) => setRouteDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm bg-gray-50 hover:bg-white focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
                  />
                </div>

                {/* Resource selection */}
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                    Select Resources ({selectedResourceIds.size} selected)
                  </label>
                  <div className="space-y-1 max-h-[400px] overflow-y-auto">
                    {activeResources.length === 0 ? (
                      <p className="text-sm text-gray-400 py-3">No active resources found</p>
                    ) : (
                      activeResources.map((resource) => {
                        const isSelected = selectedResourceIds.has(resource.id);
                        return (
                          <button
                            key={resource.id}
                            onClick={() => toggleResourceSelection(resource.id)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-left ${
                              isSelected ? 'bg-blue-50 border border-blue-200' : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                            }`}
                          >
                            <span className={`flex items-center justify-center h-5 w-5 rounded border flex-shrink-0 ${
                              isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300 bg-white'
                            }`}>
                              {isSelected && (
                                <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              )}
                            </span>
                            <div className="flex items-center gap-2 min-w-0">
                              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                                {resource.avatar_url ? (
                                  <img src={resource.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                                ) : (
                                  <span className="text-xs font-bold text-gray-500">
                                    {getPersonInitials(resource.first_name, resource.last_name)}
                                  </span>
                                )}
                              </div>
                              <span className="text-sm font-medium text-gray-800 truncate">
                                {resource.first_name} {resource.last_name}
                              </span>
                            </div>
                          </button>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                <button
                  onClick={handleApplyRoute}
                  disabled={selectedResourceIds.size === 0 || isLoadingRoute}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 active:scale-[0.98] transition-all font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoadingRoute ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Loading Route...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                      </svg>
                      Apply Route
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Address Search Modal */}
      {showAddressModal && createPortal(
        <div
          className="fixed inset-0 bg-black/50 z-[99999] flex items-start justify-center pt-[15vh]"
          style={{ backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)' }}
          onClick={() => setShowAddressModal(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-md mx-4 overflow-hidden"
            style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="px-5 pt-5 pb-3">
              <h3 className="text-base font-bold text-gray-900 mb-1">Add Pin by Address</h3>
              <p className="text-xs text-gray-500">Search for an address to place a sales pin.</p>
            </div>
            <div className="px-5 pb-4">
              <input
                ref={addressInputRef}
                type="text"
                value={addressInput}
                onChange={e => setAddressInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && addressInput.trim()) handleAddressPinSubmit(); }}
                placeholder="Enter address..."
                autoFocus
                className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors"
              />
            </div>
            <div className="px-5 pb-5 flex gap-2">
              <button
                onClick={() => setShowAddressModal(false)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddressPinSubmit}
                disabled={!addressInput.trim() || isGeocodingAddress}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isGeocodingAddress ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Finding...
                  </>
                ) : (
                  'Find & Place Pin'
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Opportunity details for a linked sales pin */}
      {viewLeadOpportunityId != null && (
        <CrmEmbeddedOpportunityModal
          opportunityId={viewLeadOpportunityId}
          onClose={() => setViewLeadOpportunityId(null)}
        />
      )}
    </div>
  );
}
